import { FileDownloader } from 'utils/fileDownloader';
import { getReportFileName, ORIGINAL_DATE_FORMAT } from 'utils/analyticsHelpers';
import { AudienceAnalyticsReport, AudienceStatsFilterv1 } from 'modules/insights';
import * as htmlToImage from 'html-to-image';
import moment from 'moment';

export class ScreenShotDownloadService {
    /**
     * Captures a screenshot of a DOM element by its ID and returns it as a PNG data URL.
     * Adds a fixed padding around the element and sets a white background for the image.
     *
     * @param reportId - The ID of the DOM element to capture.
     * @param padding - The amount of padding (in pixels) to add around the element in the screenshot. Default is 0.
     * @returns A promise that resolves to a PNG image as a data URL, or an empty string if the element is not found.
     *
     * This method uses the html-to-image library to render the element to a PNG image.
     * Padding is added by increasing the output image's width and height, and shifting the element using a CSS transform.
     * The background is set to white to ensure the screenshot is not transparent.
     */
    static async fetchDataAsImage(reportId: string, padding: number = 0): Promise<string> {
        const element = document.getElementById(reportId);
        
        if (element) {
            return await htmlToImage.toPng(element, { 
                skipFonts: true,
                width: element.offsetWidth + (padding * 2),
                height: element.offsetHeight + (padding * 2),
                style: {
                    transform: `translate(${padding}px, ${padding}px)`,
                    margin: '0',
                },
                backgroundColor: '#ffffff' 
            });
        }

        return "";
    }

    static async downloadSingleImage(reportType: string, reportFilter: Partial<AudienceStatsFilterv1>, padding: number = 0) {
        const imageData = await this.fetchDataAsImage(reportType, padding);
        const reportName = getReportFileName(reportType);

        let dateFormat = "MMM YYYY";
        
        const startDate = moment(reportFilter.startDate, ORIGINAL_DATE_FORMAT).format(dateFormat);
        const endDate = moment(reportFilter.endDate, ORIGINAL_DATE_FORMAT).format(dateFormat);

        const file = {
            name: `${reportName} ${startDate} to ${endDate}`,
        };

        if (imageData) {
            new FileDownloader(file).downloadProvided(imageData);
        }
    }

    static assembleReportFileName(reportFilter: Partial<AudienceStatsFilterv1>, reportType: string): string {
        let dateFormat = "DD-MMM-YYYY";
        let template = (start: string, end: string) => `Sparrow Connected - ${start} to ${end}.zip`;

        switch (reportType) {
            case AudienceAnalyticsReport.allAudienceAnalytics:
                dateFormat = "MMM-DD";
                template = (start, end) => `Audience Analytics - ${start} to ${end}.zip`;
                break;
        }

        const startDate = moment(reportFilter.startDate, ORIGINAL_DATE_FORMAT).format(dateFormat);
        const endDate = moment(reportFilter.endDate, ORIGINAL_DATE_FORMAT).format(dateFormat);
        return template(startDate, endDate);
    }
}